import React from "react";
import Card from "../../ui/Card";
import Skeleton from "../../ui/Skeleton";

/**
 * Geometria realinhada ao PostRanking.tsx real: rounded-xl (não rounded-3xl,
 * que é o radius default do Card quando não sobrescrito), border-purple-200
 * (não border-purple-100), gap-2/space-y-2 (não gap-4/space-y-6 — a família
 * inteira de páginas de Insights padronizou em gap-2), min-h-[160px] nos
 * cards do pódio (igual aos cards reais) e padding compacto (p-3/px-4 py-2.5,
 * como usado no restante do componente real), em vez de p-5/px-5 py-3.
 */
const PostRankingSkeleton: React.FC = () => {
  return (
    <div className="space-y-2 animate-in fade-in duration-300 w-full flex flex-col">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-purple-100 pb-2 mb-3 shrink-0">
        <div className="flex items-center gap-3">
          <Skeleton
            variant="rectangular"
            width={40}
            height={40}
            className="rounded-xl"
          />
          <div className="space-y-1.5">
            <Skeleton
              variant="rectangular"
              width={130}
              height={13}
              className="rounded-sm"
            />
            <Skeleton
              variant="rectangular"
              width={90}
              height={10}
              className="rounded-sm"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Dropdown de ordenação skeleton */}
          <Skeleton
            variant="rectangular"
            width={130}
            height={32}
            className="rounded-lg"
          />
          {/* Botão Atualizar skeleton */}
          <Skeleton
            variant="rectangular"
            width={96}
            height={32}
            className="rounded-lg"
          />
        </div>
      </div>

      {/* Pódio — mesmo min-h-[160px]/border-2/rounded-xl dos cards reais */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 shrink-0">
        {[1, 2, 3].map((i) => (
          <Card
            key={i}
            className="border-2! border-purple-200! flex flex-col min-h-[160px] [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div]:justify-between [&>div]:gap-2"
          >
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div className="flex items-center gap-2.5">
                <Skeleton
                  variant="rectangular"
                  width={28}
                  height={28}
                  className="rounded-lg"
                />
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={10}
                  className="rounded-sm"
                />
              </div>
              <Skeleton
                variant="rectangular"
                width={40}
                height={20}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2 flex-1">
              <Skeleton
                variant="rectangular"
                width="100%"
                height={10}
                className="rounded-sm"
              />
              <Skeleton
                variant="rectangular"
                width="80%"
                height={10}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-2 pt-2 border-t border-purple-200/50 shrink-0">
              {[1, 2, 3].map((j) => (
                <Skeleton
                  key={j}
                  variant="rectangular"
                  width={32}
                  height={10}
                  className="rounded-sm"
                />
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Tabela: posições 4+ — p-0! igual ao real */}
      <Card
        variant="default"
        size="sm"
        className="border-purple-200! overflow-hidden p-0! shadow-xs flex flex-col"
      >
        <div className="px-4 py-2 border-b border-purple-200 bg-purple-50/20 flex items-center justify-between shrink-0">
          <Skeleton
            variant="rectangular"
            width={80}
            height={10}
            className="rounded-sm"
          />
          <Skeleton
            variant="rectangular"
            width={40}
            height={10}
            className="rounded-sm"
          />
        </div>
        <div className="divide-y divide-purple-100">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <Skeleton
                variant="rectangular"
                width={16}
                height={10}
                className="rounded-sm shrink-0"
              />
              <Skeleton
                variant="rectangular"
                width={24}
                height={9}
                className="rounded-sm shrink-0"
              />
              <Skeleton
                variant="rectangular"
                width={14}
                height={14}
                className="rounded-sm shrink-0"
              />
              <Skeleton
                variant="rectangular"
                width="60%"
                height={10}
                className="rounded-sm flex-1"
              />
              <Skeleton
                variant="rectangular"
                width={50}
                height={10}
                className="rounded-sm shrink-0"
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default PostRankingSkeleton;
